/**
 * Writers' room. Four text steps, in order, on one provider at a time.
 * The panel never sees the role names. Tests inject `complete` so this
 * file does not call the network.
 */

import { CONTINUING_ON_COMPUTER, TEXT_UNAVAILABLE, WRITING } from './reelCopy';
import {
  QueueBodyUnconfirmed,
  ProviderError,
  type ProviderFailure,
} from './reelProviders';
import {
  mergeCameraShots,
  mergeWriterLines,
  parseDirector,
  parseReview,
  type ReelTreatment,
} from './reelTreatment';

export type TextProvider = 'groq' | 'gemini' | 'ollama';

export interface WritersRoomDeps {
  /** Configured providers, Groq first when its key exists. */
  providers: TextProvider[];
  complete: (provider: TextProvider, system: string, user: string) => Promise<string>;
  /** Fired each time a provider is about to be asked. The panel uses this for status. */
  onProvider?: (provider: TextProvider) => void;
}

export interface WritersRoomResult {
  ok: boolean;
  /** Story so far. Null when the first step never produced one. */
  treatment: ReelTreatment | null;
  error?: string;
}

const LINES_KEPT = 'Your lines are still here.';

const DIRECTOR_SYSTEM = [
  'Plan a 12 to 15 second vertical reel from the idea.',
  'Return JSON only, with no markdown.',
  'Shape: {"title":"","hook":"","tone":"","visualStyle":"","picture":"still","beats":[{"index":1,"role":"hook"}]}',
  'Use 3 to 5 beats. Each role is one of hook, build, turn, payoff.',
  'picture is still when the subject can hold still, such as a galaxy, nebula, landscape, or an object on a table.',
  'picture is moving when a person, animal, or object must change pose.',
  'Do not include shot prompts or on-screen lines.',
].join(' ');

const WRITER_SYSTEM = [
  'Write the line the viewer reads for each beat.',
  'Return JSON only: {"beats":[{"index":1,"line":""}]}',
  'One non-empty line per beat index you were given. No strategy and no shot prompts.',
].join(' ');

const CAMERA_SYSTEM = [
  'Write one picture prompt per beat.',
  'Return JSON only: {"beats":[{"index":1,"shot":""}]}',
  'Each shot names the subject, a single motion, and the camera, and repeats visualStyle.',
  'No words in the picture. No named characters.',
].join(' ');

const REVIEW_SYSTEM = [
  'Check the story. Return JSON only.',
  '{"pass":true} when shot 1 is the hook, every shot is a single motion, no shot asks for text or a known character, and every shot repeats visualStyle.',
  'Otherwise {"pass":false,"notes":"","treatment":{...}} with a full corrected story: title, hook, tone, visualStyle, picture, and beats that each have role, line, and shot.',
].join(' ');

/** Status line while a provider is working. The home computer adds a second sentence. */
export function statusWhileWriting(provider: TextProvider): string {
  if (provider === 'ollama') return `${WRITING} ${CONTINUING_ON_COMPUTER}`;
  return WRITING;
}

function kept(message: string): string {
  return `${message} ${LINES_KEPT}`;
}

async function ask<T>(
  deps: WritersRoomDeps,
  system: string,
  user: string,
  parse: (text: string) => T | null
): Promise<{ value: T } | { error: string }> {
  if (deps.providers.length === 0) return { error: TEXT_UNAVAILABLE };

  for (const provider of deps.providers) {
    let badJson = 0;
    while (badJson < 2) {
      deps.onProvider?.(provider);
      let text: string;
      try {
        text = await deps.complete(provider, system, user);
      } catch (err) {
        if (err instanceof QueueBodyUnconfirmed) return { error: err.message };
        const failure: ProviderFailure | null = err instanceof ProviderError ? err.failure : 'down';
        if (failure === 'bad-json') {
          badJson += 1;
          if (badJson >= 2) return { error: kept('Could not read a story from that reply.') };
          continue;
        }
        break;
      }
      const value = parse(text);
      if (!value) {
        badJson += 1;
        if (badJson >= 2) return { error: kept('Could not read a story from that reply.') };
        continue;
      }
      return { value };
    }
  }

  return { error: kept('Could not write this reel.') };
}

/**
 * Director, then lines, then shots, then one review.
 * A bad JSON reply retries the same provider once and does not switch.
 * A limit or outage switches to the next provider for that step only.
 */
export async function runWritersRoom(
  idea: string,
  deps: WritersRoomDeps
): Promise<WritersRoomResult> {
  const director = await ask(deps, DIRECTOR_SYSTEM, idea.trim(), parseDirector);
  if ('error' in director) return { ok: false, treatment: null, error: director.error };

  let treatment = director.value;
  const lines = await ask(
    deps,
    WRITER_SYSTEM,
    JSON.stringify({ idea: idea.trim(), story: treatment }),
    (text) => mergeWriterLines(treatment, text)
  );
  if ('error' in lines) return { ok: false, treatment, error: lines.error };
  treatment = lines.value;

  const shots = await ask(
    deps,
    CAMERA_SYSTEM,
    JSON.stringify({ idea: idea.trim(), story: treatment }),
    (text) => mergeCameraShots(treatment, text)
  );
  if ('error' in shots) return { ok: false, treatment, error: shots.error };
  treatment = shots.value;

  const reviewUser = JSON.stringify(treatment);
  const first = await ask(deps, REVIEW_SYSTEM, reviewUser, parseReview);
  if ('error' in first) return { ok: false, treatment, error: first.error };
  if (first.value.pass) return { ok: true, treatment };
  if (first.value.treatment) return { ok: true, treatment: first.value.treatment };

  const second = await ask(deps, REVIEW_SYSTEM, reviewUser, parseReview);
  if ('error' in second) return { ok: false, treatment, error: second.error };
  if (second.value.pass) return { ok: true, treatment };
  if (second.value.treatment) return { ok: true, treatment: second.value.treatment };
  return { ok: false, treatment, error: kept('Could not finish the story.') };
}
